import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { generatePrestoredColleagues } from "../../../components/Schedule/ScheduleCalendar/functions/genPrestoredColleagues.function";
import { calculateTagLimit } from "../../../components/BookingForm/BookingFilter/functions/calculateTagLimit.function";
import { namePerType } from "../../../components/FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import { useRemoteFetchScheduleColleagues } from "../../../hooks/Remote/Connections/useRemoteFetchScheduleColleagues";
import { useRemoteFetchStoredScheduleColleagues } from "../../../hooks/Remote/Connections/useRemoteFetchStoredScheduleColleagues";
import { useRemoteUpdateStoredScheduleColleagues } from "../../../hooks/Remote/Connections/useRemoteUpdateStoredScheduleColleagues";
import { useRemoteFetchBookableBookingObjects } from "../../../hooks/Remote/Schedule/useRemoteFetchBookableBookingObjects";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import { ScheduleMode } from "../../Schedule/partial/ScheduleModeToggle.partial";
import type { ScheduleResourceAutocompleteOption } from "../../Schedule/partial/ScheduleResourceAutocomplete.partial";
import { ScheduleViewMode } from "../../Schedule/partial/ScheduleViewToggle.partial";
import { Dashboard2ScheduleLayout } from "../components/Dashboard2ScheduleControls";
import { enrichTeamMembersWithPhotoUrl } from "../utils/dashboard2-schedule.utils";

export function useDashboard2ScheduleToolbarState(weekStartDate: string, weekEndDate: string) {
  const { t } = useTranslation();
  const {
    sub,
    firstName,
    surname,
    photoUrl,
    placeTypes,
    zoneTypes,
    company: {
      meta: { workplaceBookingEnabled }
    }
  } = useSelector((state: RootState) => state.login.userInformation);

  const containerRef = useRef<HTMLDivElement>(null);
  const [tagLimit, setTagLimit] = useState(2);
  const [scheduleLayout, setScheduleLayout] = useState(Dashboard2ScheduleLayout.Week);
  const [scheduleMode, setScheduleMode] = useState(ScheduleMode.Colleague);
  const [colleagues, setColleagues] = useState<TeamMember[]>([]);
  const [selectedColleagues, setSelectedColleagues] = useState<TeamMember[]>([
    { firstName, surname, userId: sub }
  ]);
  const [preStoredColleagueIds, setPreStoredColleagueIds] = useState<string[]>([]);
  const [selectedResource, setSelectedResource] =
    useState<ScheduleResourceAutocompleteOption | null>(null);
  const [resourceBookingType, setResourceBookingType] = useState<BookingType | null>(null);

  const { data: scheduleColleaguesData, isFetching: scheduleColleaguesIsFetching } =
    useRemoteFetchScheduleColleagues();
  const {
    data: storedScheduleColleaguesData,
    isFetching: storedScheduleColleaguesIsFetching,
    refetch: refetchStoredScheduleColleagues
  } = useRemoteFetchStoredScheduleColleagues();
  const { data: allUsersData } = useRemoteFetchUserInfoForAllUsers();
  const { mutateAsync: updateStoredScheduleColleagues } = useRemoteUpdateStoredScheduleColleagues();

  const isColleagueMode = scheduleMode === ScheduleMode.Colleague;
  const isResourceMode = scheduleMode === ScheduleMode.Resource;

  const scheduleViewMode =
    scheduleLayout === Dashboard2ScheduleLayout.Table
      ? ScheduleViewMode.Table
      : ScheduleViewMode.Calendar;

  const resourceAvailableTypes = useMemo(
    () => [
      ...(workplaceBookingEnabled && placeTypes.some(type => type.name === BookingType.WORKPLACE)
        ? [BookingType.WORKPLACE]
        : []),
      ...(zoneTypes.some(type => type.name === BookingType.CONFERENCEZONE)
        ? [BookingType.CONFERENCEZONE]
        : []),
      ...(placeTypes.some(type => type.name === BookingType.VEHICLE) ? [BookingType.VEHICLE] : [])
    ],
    [placeTypes, workplaceBookingEnabled, zoneTypes]
  );

  const remoteBookableBookingObjects = useRemoteFetchBookableBookingObjects({
    enabled: isResourceMode,
    endDate: weekEndDate,
    startDate: weekStartDate,
    bookingType: resourceBookingType ?? BookingType.WORKPLACE,
    includeInactive: true
  });

  const bookableBookingObjects = resourceBookingType
    ? remoteBookableBookingObjects.data?.filter(
        option => option.bookingType === resourceBookingType
      )
    : remoteBookableBookingObjects.data;

  const resourceAutocompleteOptions: ScheduleResourceAutocompleteOption[] = useMemo(
    () =>
      (bookableBookingObjects ?? []).map(option => ({
        ...option,
        searchLabel: [
          t(namePerType(option.bookingType, t)),
          option.locationName,
          option.floorName,
          option.objectName
        ]
          .filter(Boolean)
          .join(" - ")
      })),
    [bookableBookingObjects, t]
  );

  const handleStoreSelectedColleagues = useCallback(() => {
    updateStoredScheduleColleagues({ userIds: selectedColleagues.map(col => col.userId) }).then(
      () => refetchStoredScheduleColleagues()
    );
  }, [refetchStoredScheduleColleagues, selectedColleagues, updateStoredScheduleColleagues]);

  const handleResourceFetchEnable = useCallback(() => {
    setSelectedResource(null);
  }, []);

  useEffect(() => {
    if (!scheduleColleaguesData) {
      return;
    }

    const allColleagues = enrichTeamMembersWithPhotoUrl(scheduleColleaguesData, allUsersData, {
      userId: sub,
      photoUrl
    });
    setColleagues(allColleagues);

    const preStored = generatePrestoredColleagues({
      user: { userBookedById: sub, firstName, surname, userId: sub },
      sub,
      scheduleColleaguesData,
      storedScheduleColleaguesData: storedScheduleColleaguesData ?? []
    });

    setPreStoredColleagueIds(preStored.map(colleague => colleague.userId));
    setSelectedColleagues(
      enrichTeamMembersWithPhotoUrl(preStored, allUsersData, { userId: sub, photoUrl })
    );
  }, [
    allUsersData,
    firstName,
    photoUrl,
    scheduleColleaguesData,
    storedScheduleColleaguesData,
    sub,
    surname
  ]);

  useEffect(() => {
    if (!isResourceMode) {
      return;
    }
    if (resourceAvailableTypes.length === 0) {
      setResourceBookingType(null);
      return;
    }
    if (resourceBookingType && resourceAvailableTypes.includes(resourceBookingType)) {
      return;
    }
    setResourceBookingType(resourceAvailableTypes[0]);
  }, [isResourceMode, resourceAvailableTypes, resourceBookingType]);

  useEffect(() => {
    if (!isColleagueMode) {
      return;
    }
    setSelectedResource(null);
    setResourceBookingType(null);
  }, [isColleagueMode]);

  useEffect(() => {
    if (selectedColleagues.length === 0) {
      return;
    }

    const mappedColleagues: ConnectedBookingData[] = selectedColleagues.map(colleague => ({
      firstName: colleague.firstName ?? "",
      lastName: colleague.surname ?? "",
      userId: colleague.userId ?? "",
      email: colleague.email ?? "",
      bookingInventoryId: colleague.bookingInventoryId ?? 0
    }));

    const resizeObserver = new ResizeObserver(() => {
      calculateTagLimit(containerRef, 132, mappedColleagues, setTagLimit);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    calculateTagLimit(containerRef, 132, mappedColleagues, setTagLimit);

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedColleagues]);

  return {
    scheduleLayout,
    setScheduleLayout,
    scheduleMode,
    setScheduleMode,
    scheduleViewMode,
    isColleagueMode,
    isResourceMode,
    colleagues,
    selectedColleagues,
    setSelectedColleagues,
    preStoredColleagueIds,
    containerRef,
    tagLimit,
    scheduleColleaguesIsFetching,
    storedScheduleColleaguesIsFetching,
    handleStoreSelectedColleagues,
    selectedResource,
    setSelectedResource,
    resourceBookingType,
    setResourceBookingType,
    resourceAvailableTypes,
    resourceAutocompleteLoading: remoteBookableBookingObjects.isFetching,
    bookableBookingObjectsError: remoteBookableBookingObjects.isError,
    resourceAutocompleteOptions,
    handleResourceFetchEnable
  };
}
